import { buildDatasetSchema } from "../data/datasetSchema";
import { buildMetrics } from "./metricsEngine";
import { buildFindings } from "./insightEngine";
import {
  generateFindingInterpretations,
  generateDetailedReportContent,
  generateExecutiveSummaryContent,
} from "./narrativeGenerator";
import { buildSalesAnalysisReportPdf } from "./salesAnalysisReportBuilder";
import { buildExecutiveSummaryPdf } from "./executiveSummaryBuilder";
import { reportFileName } from "./pdfHelpers";

// ============================================================
// REPORT GENERATION ARCHITECTURE
// ============================================================
//   DATASET
//     -> DATA PROFILING (buildDatasetSchema — column roles, never sent raw)
//     -> METRICS ENGINE (buildMetrics — every number, calculated locally)
//     -> INSIGHT DETECTION (buildFindings — FACT/CORRELATION-tagged findings)
//     -> REPORT-SPECIFIC CONTENT (narrativeGenerator — Groq writes prose
//        ONLY on top of the metrics/findings above, never sees raw rows,
//        never invents a number)
//     -> PDF RENDERER (built entirely locally with jsPDF; Groq never
//        touches the file — it only ever returns text/JSON)
//
// The metrics/findings layer is shared between both report types; only the
// content generation and PDF layout differ, matching what each audience
// actually needs.

function download(doc, filename) {
  doc.save(filename);
}

export async function generateSalesAnalysisReport(session, onProgress = () => {}, { watermark = true } = {}) {

  onProgress("Profiling dataset...");
  const schema = session.schema || buildDatasetSchema(session.dataset);

  onProgress("Calculating verified metrics...");
  const metrics = buildMetrics(session.dataset, schema, session.name);

  onProgress("Detecting findings...");
  const findings = buildFindings(metrics);

  onProgress("Writing key findings...");
  const interpretations = await generateFindingInterpretations(findings);

  onProgress("Writing recommendations...");
  const content = await generateDetailedReportContent(metrics, findings);

  onProgress("Building PDF...");
  const doc = buildSalesAnalysisReportPdf(metrics, findings, interpretations, content, { watermark });

  download(doc, reportFileName("Sales_Analysis_Report", session.name));

  onProgress(null);
}

export async function generateExecutiveSummary(session, onProgress = () => {}, { watermark = true } = {}) {

  onProgress("Profiling dataset...");
  const schema = session.schema || buildDatasetSchema(session.dataset);

  onProgress("Calculating verified metrics...");
  const metrics = buildMetrics(session.dataset, schema, session.name);

  onProgress("Detecting findings...");
  const findings = buildFindings(metrics);

  onProgress("Writing executive summary...");
  const content = await generateExecutiveSummaryContent(metrics, findings);

  onProgress("Building PDF...");
  const doc = buildExecutiveSummaryPdf(metrics, content, { watermark });

  download(doc, reportFileName("Executive_Summary", session.name));

  onProgress(null);
}
